// src/contexts/AuthContext.js

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { 
  API_ENDPOINT, 
  ENDPOINTS, 
  ERROR_MESSAGES,
  TOKEN_KEY, 
  REFRESH_TOKEN_KEY, 
  TOKEN_EXPIRY_KEY 
} from '../constants';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      setIsAuthenticated(true);
    }
    setIsLoading(false);

    return () => {
      // Cleanup function
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    };
  }, []);

  const login = async (companyCode, username, password) => {
    try {
      setAuthError(null);
      const response = await fetch(`${API_ENDPOINT}${ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyCode,
          username,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || ERROR_MESSAGES.LOGIN_FAILED);
      }

      const { accessToken, expireAt, refreshToken } = data.data;

      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expireAt);

      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.LOGIN_FAILED
      };
    }
  };

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      await fetch(`${API_ENDPOINT}${ENDPOINTS.LOGOUT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken,
        }),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      setIsAuthenticated(false);
      setAuthError(null);
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      const response = await fetch(`${API_ENDPOINT}${ENDPOINTS.REFRESH_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken,
        }),
      });

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.TOKEN_REFRESH_FAILED);
      }

      const data = await response.json();
      const { accessToken, expireAt } = data.data;

      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expireAt);

      return accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      return null;
    }
  }, [logout]);

  // Token refresh scheduler
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTokenExpiry = async () => {
      const expiryTime = new Date(localStorage.getItem(TOKEN_EXPIRY_KEY));
      const currentTime = new Date();
      const timeUntilExpiry = expiryTime - currentTime;

      // Refresh token 1 minute before expiry
      if (timeUntilExpiry > 0 && timeUntilExpiry <= 60000) {
        await refreshAccessToken();
      }
    };

    const interval = setInterval(checkTokenExpiry, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAccessToken]);

  const value = {
    isAuthenticated,
    isLoading,
    authError,
    login,
    logout,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;